import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useTheme,
} from '@mui/material';
import { DateTime } from 'luxon';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StringDatePicker } from '../../../../components/input/string-date-picker';
import { getLocale } from '../../../../utils/i18n';
import { blockPendingRenderDates } from '../block-drag';
import { startDateShiftErrorKey } from '../annual-planner-access';

/**
 * The Confirm New Start Date modal the Timeline block drag opens (Phase 04).
 *
 * WAI-ARIA Dialog pattern: role alertdialog, labelled by the title, focus
 * moves in on open and is trapped (MUI Modal), Escape cancels via onClose.
 * The parent owns returning focus to the block that was dragged.
 *
 * The modal is confirm-only: it never saves. Confirm hands the (possibly
 * picker-adjusted) start date back; the move itself rides the shared shift
 * path from Phase 01.
 */
export function ConfirmBlockMoveModal({
  block,
  proposedStartDate,
  isSaving,
  onCancel,
  onConfirm,
}: {
  block: Pick<
    import('../types').Block,
    'name' | 'startDate' | 'endDate' | 'layout'
  >;
  /** The start date the drop proposed; also the picker's initial value. */
  proposedStartDate: string;
  isSaving?: boolean;
  onCancel: () => void;
  onConfirm: (startDate: string) => void;
}) {
  const { t } = useTranslation();
  const {
    palette: { mediumEmphasisTextLight },
  } = useTheme();

  const [startDate, setStartDate] = useState(proposedStartDate);

  // WAI-ARIA Dialog pattern: focus moves into the modal when it opens. MUI's
  // default focuses its transition container, which wraps (not contains) the
  // alertdialog, so the focus target is the dialog body itself. The deferred
  // focus lands after MUI's own mount focus, which would otherwise win (the
  // parent Modal's effect runs last). The focus trap (MUI Modal) keeps focus
  // inside from there.
  const contentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const id = window.setTimeout(() => contentRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, []);

  // A new drag proposes a new date; the picker follows the drop.
  useEffect(() => {
    setStartDate(proposedStartDate);
  }, [proposedStartDate]);

  // "Today" is the UTC calendar date, the same value the API checks, so the
  // rules agree near UTC midnight instead of the browser's local midnight.
  const todayISO = DateTime.utc().toISODate() ?? '';
  const today = DateTime.fromISO(todayISO);

  // Same past-move rule as the Edit Block Drawer (shared
  // startDateShiftErrorKey): a block that starts in the past may only move
  // forward; a future block may not enter the past.
  const startDateMin =
    block.startDate < todayISO ? DateTime.fromISO(block.startDate) : today;

  const errorKey = startDate
    ? startDateShiftErrorKey(block.startDate, startDate, todayISO)
    : // An emptied picker just disables Confirm; the block cannot save
      // without a date.
      null;

  const formatDate = (isoDate: string) =>
    DateTime.fromISO(isoDate).toLocaleString(DateTime.DATE_SHORT, {
      locale: getLocale(),
    });

  const newEndDate = startDate
    ? (() => {
        const _startDate = DateTime.fromISO(startDate);
        if (!_startDate.isValid) return null;
        // The preview shows the stored end date shifted by the offset — the
        // exact value the server writes (getShiftedEndDate), never a value
        // recomputed from the layout, which can diverge after manual edits.
        return blockPendingRenderDates(block, startDate).endDate;
      })()
    : null;

  const titleId = 'confirm-block-move-title';
  // A picked date equal to the current start date is a no-op: Block.Edit
  // would still rewrite the block and its focuses without shifting anything.
  const canConfirm =
    !!startDate && startDate !== block.startDate && !errorKey && !isSaving;

  return (
    <Dialog
      open
      // Escape and the backdrop must not cancel mid-save: cancelling would
      // clear the pending move while the edit is still in flight.
      onClose={isSaving ? undefined : onCancel}
      aria-labelledby={titleId}
      PaperProps={{ role: 'alertdialog', 'aria-labelledby': titleId }}
    >
      <DialogTitle id={titleId}>{t('confirmNewStartDate')}</DialogTitle>
      <DialogContent
        ref={contentRef}
        tabIndex={-1}
        className="flex flex-col gap-4"
      >
        <Typography variant="body2">
          {t('confirmNewStartDateMessage', { name: block.name })}
        </Typography>
        <Typography variant="body2" color={mediumEmphasisTextLight}>
          {t('currentDateRange', {
            start: formatDate(block.startDate),
            end: formatDate(block.endDate),
          })}
        </Typography>
        {newEndDate && startDate !== block.startDate && (
          <Typography variant="body2" aria-live="polite">
            {t('newDateRange', {
              start: formatDate(startDate),
              end: formatDate(newEndDate),
            })}
          </Typography>
        )}
        <StringDatePicker
          label={t('startDate')}
          onChange={setStartDate}
          value={startDate}
          error={!!errorKey}
          errorMessage={errorKey ? t(errorKey) : undefined}
          minDate={startDateMin}
          sx={{ width: '100%' }}
        />
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onCancel} disabled={isSaving}>
          {t('cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={() => onConfirm(startDate)}
          disabled={!canConfirm}
        >
          {t('confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
